/**
 * src/components/users/UserCard.tsx
 * Card component for displaying individual user profile, balance, and management options
 */

import React, { useState } from 'react';
import { 
  User, 
  ShieldCheck, 
  ArrowUpRight, 
  ArrowDownLeft, 
  CheckCircle2, 
  MoreVertical, 
  Edit2, 
  MessageCircle, 
  QrCode,
  UserCheck,
  UserX,
  AlertTriangle,
  Bell,
  KeyRound
} from 'lucide-react';
import { api, type UserItem } from '../../services/api';
import { GlassCard } from '../ui/GlassCard';
import { GlassBadge } from '../ui/GlassBadge';
import { generateWhatsAppReminderUrl } from '../../utils/whatsapp';
import { useToast } from '../../context/ToastContext';
import { haptic, playSuccessSound, playTapSound } from '../../utils/audioHaptics';

interface UserCardProps {
  user: UserItem;
  onEdit?: (user: UserItem) => void;
  onChangePassword?: (user: UserItem) => void;
  onViewStatement?: (userId: number) => void;
  onSettleUp?: (user: UserItem) => void;
  onToggleStatus?: (user: UserItem) => void;
}

export const UserCard: React.FC<UserCardProps> = ({
  user,
  onEdit,
  onChangePassword,
  onViewStatement,
  onSettleUp,
  onToggleStatus
}) => {
  const balance = user.current_balance ?? 0;
  const isPositive = balance > 0;
  const isNegative = balance < 0;
  const isDeactivated = user.is_active === 0;

  const [isSendingRemind, setIsSendingRemind] = useState(false);
  const { showToast } = useToast();

  const handleSendInAppReminder = async () => {
    if (isSendingRemind) return;
    haptic.tap();
    playTapSound();
    setIsSendingRemind(true);
    try {
      await api.notifications.remind({
        target_user_id: user.id,
        amount: Math.abs(balance)
      });
      haptic.success();
      playSuccessSound();
      showToast(`In-app push reminder sent to ${user.full_name || user.username}! They will receive it in their PWA.`, 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to send in-app reminder', 'error');
    } finally {
      setIsSendingRemind(false);
    }
  };

  const formatAmount = (val: number) => {
    return Math.abs(val).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <div
      className="ios-inset-group"
      style={{
        padding: '18px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: '14px',
        opacity: isDeactivated ? 0.72 : 1,
        border: isDeactivated ? '1px dashed rgba(255, 69, 58, 0.4)' : undefined
      }}
    >
      {/* Top Header */}
      <div className="flex-between">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            className={`ios-icon-squircle ${user.role === 'admin' ? 'blue' : isDeactivated ? 'red' : 'green'}`}
            style={{
              width: '42px',
              height: '42px',
              borderRadius: '13px',
              fontWeight: 800,
              fontSize: '1rem',
              overflow: 'hidden'
            }}
          >
            {user.avatar_url ? (
              <img src={user.avatar_url} alt={user.full_name || user.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              (user.full_name || user.username).charAt(0).toUpperCase()
            )}
          </div>

          <div>
            <div style={{ 
              fontWeight: 700, 
              fontSize: '1.02rem', 
              color: 'var(--text-primary)', 
              letterSpacing: '-0.015em',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>{user.full_name || user.username}</span>
            </div>
            {user.full_name && (
              <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: '1px' }}>
                @{user.username}
              </div>
            )}
            <div style={{ marginTop: '3px', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{
                fontSize: '0.65rem',
                fontWeight: 700,
                padding: '1px 7px',
                borderRadius: 'var(--radius-pill)',
                background: user.role === 'admin' ? 'rgba(10, 132, 255, 0.15)' : 'rgba(48, 209, 88, 0.15)',
                color: user.role === 'admin' ? 'var(--ios-blue)' : 'var(--ios-green)',
                textTransform: 'capitalize'
              }}>
                {user.role}
              </span>

              <span style={{
                fontSize: '0.65rem',
                fontWeight: 700,
                padding: '1px 7px',
                borderRadius: 'var(--radius-pill)',
                background: isDeactivated ? 'rgba(255, 69, 58, 0.16)' : 'rgba(48, 209, 88, 0.16)',
                color: isDeactivated ? 'var(--ios-red)' : 'var(--ios-green)'
              }}>
                {isDeactivated ? 'Deactivated' : 'Active'}
              </span>

              {user.whatsapp_phone && (
                <span style={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  padding: '1px 7px',
                  borderRadius: 'var(--radius-pill)',
                  background: 'rgba(48, 209, 88, 0.12)',
                  color: 'var(--ios-green)',
                  border: '0.5px solid rgba(48, 209, 88, 0.25)'
                }} title={`WhatsApp: ${user.whatsapp_phone}`}>
                  WA: {user.whatsapp_phone}
                </span>
              )}

              {user.upi_id && (
                <span style={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  padding: '1px 7px',
                  borderRadius: 'var(--radius-pill)',
                  background: 'rgba(66, 133, 244, 0.12)',
                  color: '#4285F4',
                  border: '0.5px solid rgba(66, 133, 244, 0.25)'
                }} title={`Google Pay UPI ID: ${user.upi_id}`}>
                  GPay: {user.upi_id.length > 18 ? user.upi_id.slice(0, 16) + '...' : user.upi_id}
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {onToggleStatus && user.role !== 'admin' && (
            <button
              type="button"
              onClick={() => onToggleStatus(user)}
              style={{
                background: isDeactivated ? 'rgba(48, 209, 88, 0.15)' : 'rgba(255, 69, 58, 0.15)',
                border: 'none',
                color: isDeactivated ? 'var(--ios-green)' : 'var(--ios-red)',
                cursor: 'pointer',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all var(--duration-fast)'
              }}
              title={isDeactivated ? 'Reactivate member account' : 'Deactivate member account'}
            >
              {isDeactivated ? <UserCheck size={15} /> : <UserX size={15} />}
            </button>
          )}

          {onChangePassword && (
            <button
              type="button"
              onClick={() => {
                haptic.tap();
                playTapSound();
                onChangePassword(user);
              }}
              style={{
                background: 'rgba(10, 132, 255, 0.15)',
                border: 'none',
                color: 'var(--ios-blue)',
                cursor: 'pointer',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all var(--duration-fast)'
              }}
              title="Change password"
            >
              <KeyRound size={14} strokeWidth={2.4} />
            </button>
          )}

          {onEdit && (
            <button
              onClick={() => onEdit(user)}
              style={{
                background: 'rgba(118, 118, 128, 0.18)',
                border: 'none',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all var(--duration-fast)'
              }}
              title="Edit member"
            >
              <Edit2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Deactivated Notice Banner */}
      {isDeactivated && (
        <div style={{
          padding: '8px 12px',
          borderRadius: '10px',
          background: 'rgba(255, 69, 58, 0.12)',
          border: '1px solid rgba(255, 69, 58, 0.25)',
          color: 'var(--ios-red)',
          fontSize: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          lineHeight: 1.3
        }}>
          <AlertTriangle size={14} style={{ flexShrink: 0 }} />
          <span>Account deactivated. Login blocked & excluded from bill splits.</span>
        </div>
      )}

      {/* Balance Display */}
      <div
        style={{
          padding: '12px 14px',
          borderRadius: '14px',
          background: 'rgba(118, 118, 128, 0.12)',
          border: 'var(--border-hairline)'
        }}
      >
        <div style={{ fontSize: '0.6875rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: '2px' }}>
          Current Balance
        </div>

        <div className="flex-between">
          <div
            className="num-font"
            style={{
              fontSize: '1.25rem',
              fontWeight: 800,
              color: isPositive ? 'var(--ios-green)' : isNegative ? 'var(--ios-red)' : 'var(--text-primary)',
              letterSpacing: '-0.02em'
            }}
          >
            {isNegative ? '-' : isPositive ? '+' : ''}₹{formatAmount(balance)}
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            fontSize: '0.75rem',
            fontWeight: 700,
            color: isPositive ? 'var(--ios-green)' : isNegative ? 'var(--ios-red)' : 'var(--ios-blue)'
          }}>
            {isPositive && <ArrowUpRight size={13} strokeWidth={2.5} />}
            {isNegative && <ArrowDownLeft size={13} strokeWidth={2.5} />}
            {balance === 0 && <CheckCircle2 size={13} />}
            <span>{isPositive ? 'Creditor' : isNegative ? 'Debtor' : 'Settled'}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons Row for Reminders & Quick Settlement */}
      {isNegative && (
        <div style={{ display: 'grid', gridTemplateColumns: onSettleUp ? '1fr 1fr 1fr' : '1fr 1fr', gap: '6px' }}>
          <a
            href={generateWhatsAppReminderUrl({
              name: user.full_name || user.username,
              amountOwed: balance,
              phone: user.whatsapp_phone || undefined
            })}
            target="_blank"
            rel="noopener noreferrer"
            className="ios-tap-target"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '8px 6px',
              borderRadius: 'var(--radius-button)',
              background: 'rgba(48, 209, 88, 0.12)',
              border: '1px solid rgba(48, 209, 88, 0.25)',
              color: 'var(--ios-green)',
              fontSize: '0.74rem',
              fontWeight: 700,
              textDecoration: 'none',
              cursor: 'pointer'
            }}
            title="Send polite WhatsApp payment reminder"
          >
            <MessageCircle size={13} />
            <span>WhatsApp</span>
          </a>

          <button
            type="button"
            onClick={handleSendInAppReminder}
            disabled={isSendingRemind}
            className="ios-tap-target"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '8px 6px',
              borderRadius: 'var(--radius-button)',
              background: 'rgba(191, 90, 242, 0.14)',
              border: '1px solid rgba(191, 90, 242, 0.3)',
              color: '#bf5af2',
              fontSize: '0.74rem',
              fontWeight: 700,
              cursor: isSendingRemind ? 'not-allowed' : 'pointer',
              opacity: isSendingRemind ? 0.6 : 1
            }}
            title="Send in-app & PWA push payment reminder"
          >
            <Bell size={13} />
            <span>{isSendingRemind ? 'Sending...' : 'In-App'}</span>
          </button>

          {onSettleUp && (
            <button
              type="button"
              onClick={() => onSettleUp(user)}
              className="ios-tap-target"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                padding: '8px 6px',
                borderRadius: 'var(--radius-button)',
                background: 'rgba(10, 132, 255, 0.12)',
                border: '1px solid rgba(10, 132, 255, 0.25)',
                color: 'var(--ios-blue)',
                fontSize: '0.74rem',
                fontWeight: 700,
                cursor: 'pointer'
              }}
              title="Open UPI QR and Settle Up"
            >
              <QrCode size={13} />
              <span>Settle</span>
            </button>
          )}
        </div>
      )}

      {/* Settle Member Credit Payout Button (Admin paying member) */}
      {isPositive && onSettleUp && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
          <button
            type="button"
            onClick={() => onSettleUp(user)}
            className="ios-tap-target"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '9px 12px',
              borderRadius: 'var(--radius-button)',
              background: 'rgba(52, 168, 83, 0.12)',
              border: '1px solid rgba(52, 168, 83, 0.28)',
              color: 'var(--ios-green)',
              fontSize: '0.8rem',
              fontWeight: 700,
              cursor: 'pointer'
            }}
            title="Settle credit payout to member via Google Pay"
          >
            <QrCode size={14} />
            <span>Settle Payout (GPay)</span>
          </button>
        </div>
      )}

      {/* Footer Statement Link */}
      {onViewStatement && (
        <button
          onClick={() => onViewStatement(user.id)}
          className="ios-tap-target"
          style={{
            width: '100%',
            fontSize: '0.8125rem',
            padding: '8px',
            borderRadius: 'var(--radius-button)',
            background: 'rgba(255, 255, 255, 0.04)',
            border: 'var(--border-hairline)',
            color: 'var(--text-secondary)',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          View Statement →
        </button>
      )}
    </div>
  );
};

